import React, { useEffect, useState } from 'react';
import {
  Typography, Select, Button, Descriptions, Input, Space, message, Card, Tag,
} from 'antd';
import { SearchOutlined, AppstoreOutlined } from '@ant-design/icons';
import { fetchUsers, createPermissionSet, fetchPermissionCatalog } from '../api';
import PermissionTreeEditor from '../components/PermissionTreeEditor';
import { useDashboardOptions, SCOPE_OPTIONS } from '../../moduleDashboards/constants';

const { Title } = Typography;
const { Option } = Select;

const CreatePermissionSetPage = () => {
  const [permissions, setPermissions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [permissionSetName, setPermissionSetName] = useState('');

  // Scope: 'user' (override for one user), 'group' (reusable), 'role' (business role).
  const [scope, setScope] = useState('user');
  const [setKey, setSetKey] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(0);

  // Dashboard binding for role/group sets: which dashboard members land on + max data scope.
  const [dashboardKey, setDashboardKey] = useState(null);
  const [dashboardMaxScope, setDashboardMaxScope] = useState('BRANCH');
  // Dashboards are per role permission set, so the list comes from the server.
  const { options: dashboardOptions, loading: dashboardOptionsLoading } = useDashboardOptions();

  // Catalog drives the tree editor (source of truth, same the backend validates against).
  const [catalog, setCatalog] = useState({});
  const [modules, setModules] = useState([]);

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchPermissionCatalog();
        setCatalog(res.catalog || {});
        setModules(res.modules || []);
      } catch (err) {
        console.error('Failed to load permission catalog', err);
        messageApi.error('Failed to load permission catalog');
      }
    })();
  }, [messageApi]);

  const handleUserSearch = async () => {
    setLoading(true);
    try {
      const result = await fetchUsers({ search: userSearch });
      setUserOptions(result.data || []);
    } catch (err) {
      messageApi.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUser(userOptions.find((u) => u._id === userId));
  };

  const handleCreate = async () => {
    if (!permissionSetName) {
      messageApi.warning('Please enter a name');
      return;
    }
    if (scope === 'user' && !selectedUser) {
      messageApi.warning('Please select a user for a user-scoped set');
      return;
    }

    // The tree editor already emits the stored shape (incl. deny). Strip empty denies.
    const mappedPermissions = permissions.map((p) => {
      const deny = p.deny || {};
      const hasDeny = (deny.topLevel || []).length || (deny.viewableFields || []).length
        || (deny.editableFields || []).length;
      const out = {
        module: p.module,
        topLevel: p.topLevel || [],
        viewableFields: p.viewableFields || [],
        editableFields: p.editableFields || [],
      };
      if (hasDeny) out.deny = deny;
      return out;
    });

    const payload = {
      name: permissionSetName,
      description,
      scope,
      priority: Number(priority) || 0,
      permissions: mappedPermissions,
    };

    if (scope === 'user') {
      payload.subject = selectedUser._id;
      payload.type = 'User';
    } else {
      if (setKey) payload.key = setKey.trim().toUpperCase();
      // A role set resolves to its OWN dashboard, so it is left unbound here —
      // the Dashboard Builder writes the binding when the layout is saved. Only a
      // group set explicitly points at another role's dashboard.
      payload.dashboardKey = scope === 'role' ? null : (dashboardKey || null);
      payload.dashboardMaxScope = dashboardMaxScope || 'BRANCH';
    }

    try {
      setSaving(true);
      const data = await createPermissionSet(payload);
      if (data.message) messageApi.success(data.message);
      else if (data.failedMessage) {
        messageApi.error(data.failedMessage + (data.errors ? `: ${data.errors.join('; ')}` : ''));
      }
      if (data.message) {
        setPermissions([]);
        setPermissionSetName('');
        setSelectedUser(null);
        setDescription('');
        setSetKey('');
        setPriority(0);
        setDashboardKey(null);
        setDashboardMaxScope('BRANCH');
      }
    } catch (err) {
      messageApi.error('Failed to create permission set');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {contextHolder}
      <div style={{ padding: 24 }}>
        <Title level={3}>Create New Permission Set</Title>

        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <Space wrap>
            <Select value={scope} onChange={setScope} style={{ width: 240 }}>
              <Option value="role">Role (business role, e.g. ADMIN)</Option>
              <Option value="group">Group (reusable team set)</Option>
              <Option value="user">User override (single user)</Option>
            </Select>
            <Input
              placeholder="Permission Set Name"
              value={permissionSetName}
              onChange={(e) => setPermissionSetName(e.target.value)}
              style={{ width: 280 }}
            />
          </Space>

          <Input
            placeholder="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {scope !== 'user' && (
            <Space wrap>
              <Input
                placeholder="Key (e.g. BRANCH_MANAGER) — optional"
                value={setKey}
                onChange={(e) => setSetKey(e.target.value)}
                style={{ width: 320 }}
              />
              <Input
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                style={{ width: 160 }}
                addonBefore="Priority"
              />
            </Space>
          )}

          {scope !== 'user' && (
            <Space wrap>
              {/* A ROLE set automatically gets a dashboard of its own — you build
                  it in the Dashboard Builder once the set exists. Only a GROUP
                  set, which has no dashboard of its own, points at a role's. */}
              {scope === 'role' ? (
                <Tag color="blue" style={{ padding: '4px 10px', whiteSpace: 'normal' }}>
                  <AppstoreOutlined /> This role gets its own dashboard — design it in the Dashboard Builder after creating.
                </Tag>
              ) : (
                <Select
                  allowClear
                  placeholder="Dashboard (role landing page)"
                  value={dashboardKey || undefined}
                  onChange={(v) => setDashboardKey(v || null)}
                  style={{ width: 320 }}
                  options={dashboardOptions}
                  loading={dashboardOptionsLoading}
                  notFoundContent="No role dashboards yet"
                />
              )}
              <Select
                value={dashboardMaxScope}
                onChange={setDashboardMaxScope}
                style={{ width: 220 }}
                options={SCOPE_OPTIONS.map((s) => ({ value: s.value, label: `Max scope: ${s.label}` }))}
              />
            </Space>
          )}

          {scope === 'user' && (
            <>
              <Space>
                <Input
                  placeholder="Search by email or phone"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  style={{ width: 300 }}
                  onPressEnter={handleUserSearch}
                />
                <Button icon={<SearchOutlined />} onClick={handleUserSearch} loading={loading}>
                  Search
                </Button>
              </Space>
              <Select
                placeholder="Select user"
                value={selectedUser?._id}
                onChange={handleUserSelect}
                style={{ width: 300 }}
                filterOption={false}
              >
                {userOptions.map((user) => (
                  <Option key={user._id} value={user._id}>
                    {user.name} ({user.email || user.phone})
                  </Option>
                ))}
              </Select>
              {selectedUser && (
                <Descriptions bordered column={1} size="small">
                  <Descriptions.Item label="Subject ID">{selectedUser._id}</Descriptions.Item>
                  <Descriptions.Item label="Name">{selectedUser.name}</Descriptions.Item>
                  <Descriptions.Item label="Role">{selectedUser.role}</Descriptions.Item>
                </Descriptions>
              )}
            </>
          )}
        </Space>

        <Card title="Permissions" size="small" style={{ marginBottom: 16 }}>
          <PermissionTreeEditor
            catalog={catalog}
            modules={modules}
            value={permissions}
            onChange={setPermissions}
          />
        </Card>

        <Button type="primary" loading={saving} onClick={handleCreate}>
          Create
        </Button>
      </div>
    </>
  );
};

export default CreatePermissionSetPage;
